import React, { useEffect, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { toast } from 'sonner';
import { extractError } from '@/lib/errors';
import { X, Loader2, Printer } from 'lucide-react';
import { JewelleryTagViewer, buildTagDataFromDetail } from '@/components/jewellery/JewelleryTagViewer';
import { tagThemeClass } from './tagTheme';
import { setMatOpts, useRebuildOnOpts } from '@/lib/tagMatHandling';

// Single-piece tag viewer/print dialog for the Tag Workspace (mirrors the ERP card "Tag" button).
export function TagSingleTagDialog({ item, onClose, onLayoutSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useRebuildOnOpts(setData, buildTagDataFromDetail);
  useEffect(() => { setMatOpts([]); }, [item]);

  useEffect(() => {
    if (!item) return undefined;
    let cancelled = false;
    setLoading(true);
    setData(null);
    notesApi.post('/notes/tag-workspace/tags/batch', { jewellery_ids: [item.id] })
      .then(({ data: res }) => {
        const raw = res?.items?.[0];
        if (!cancelled && raw) setData(buildTagDataFromDetail(raw));
      })
      .catch((e) => { if (!cancelled) toast.error(extractError(e, 'Failed to load tag details')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [item]);

  if (!item) return null;
  return (
    <div className={`tag-scope ${tagThemeClass()} fixed inset-0 z-[200] flex items-center justify-center p-4`} data-testid="tagws-single-tag-dialog">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative z-10 max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl border border-border bg-card p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between">
          <h3 className="flex items-center gap-2 text-lg font-semibold tracking-tight text-foreground">
            <Printer size={18} className="text-[hsl(var(--ring))]" /> Jewellery Tag — {item.jewellery_no}
          </h3>
          <button onClick={onClose} aria-label="Close" data-testid="tagws-single-tag-close-button" className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground" data-testid="tagws-single-tag-loading"><Loader2 size={16} className="animate-spin" /> Loading tag…</div>
        ) : data ? (
          <JewelleryTagViewer
            data={data} defaultShowQr={false} piecesToggle sizeToggle
            initialLayout={item.tagws_layout && item.tagws_layout !== 'auto' ? item.tagws_layout : undefined}
            onLayoutChange={(v) => {
              notesApi.put(`/notes/tag-workspace/jewellery/${item.id}/layout`, { layout: v })
                .then(() => onLayoutSaved?.(item.id, v))
                .catch(() => {});
            }}
          />
        ) : (
          <div className="py-10 text-center text-sm text-muted-foreground">No tag to display.</div>
        )}
      </div>
    </div>
  );
}
